import React, { useState } from "react"
import { Link } from "gatsby"
import RichText from "./richText"
import Modal from "../modal"

const Card = ({ blok, isRTDPage }) => {
	const { image, title, subtitle, content, url, buttonDescription, callOut } =
		blok

	const [isModalOpen, setIsModalOpen] = useState(false)

	// Use link field if url is not available (backward compatibility)
	const urlField = url

	// Check if callOut has actual content (not just empty richtext structure)
	const hasCallOutContent = callOut => {
		if (!callOut) return false
		if (typeof callOut === "string") return callOut.trim().length > 0
		if (callOut.content && Array.isArray(callOut.content)) {
			// Check if there's actual text content in the richtext structure
			const hasText = callOut.content.some(item => {
				if (item.content && Array.isArray(item.content)) {
					return item.content.some(
						textItem => textItem.text && textItem.text.trim().length > 0
					)
				}
				return false
			})
			return hasText
		}
		return false
	}

	// Check if content has actual text (for rich text fields)
	const hasContentText = content => {
		if (!content) return false
		if (typeof content === "string") return content.trim().length > 0
		if (content.content && Array.isArray(content.content)) {
			const hasText = content.content.some(item => {
				if (item.content && Array.isArray(item.content)) {
					return item.content.some(
						textItem => textItem.text && textItem.text.trim().length > 0
					)
				}
				return false
			})
			return hasText
		}
		return false
	}

	// Determine card behavior based on URL, buttonDescription, and callOut
	const hasUrl =
		urlField &&
		(typeof urlField === "string" || urlField.cached_url || urlField.url)
	const hasButtonDescription = buttonDescription && buttonDescription.trim()
	const hasCallOut = hasCallOutContent(callOut)

	// Check if card has any text content (title, subtitle, content, or button)
	const hasAnyTextContent = 
		(title && title.trim()) ||
		(subtitle && subtitle.trim()) ||
		hasContentText(content) ||
		hasButtonDescription

	// 1. If URL is filled but button description isn't → entire block becomes a button
	// 2. If button description is filled → card isn't a button, but a CTA button appears at the bottom
	// 3. If Call out is filled → card becomes a button AND a popup appears
	const isEntireCardClickable = hasUrl && !hasButtonDescription && !hasCallOut
	const showButtonAtBottom = hasUrl && hasButtonDescription && !hasCallOut
	const showCallOutPopup = hasCallOut

	// Build href from url or link field
	let href = null
	let isExternalUrl = false
	if (hasUrl) {
		if (typeof urlField === "string") {
			href = urlField
			isExternalUrl =
				urlField.startsWith("http://") || urlField.startsWith("https://")
		} else if (urlField.linktype === "story") {
			href = `/${urlField.cached_url}`
		} else {
			href = urlField.cached_url || urlField.url || urlField
			isExternalUrl = href.startsWith("http://") || href.startsWith("https://")
		}
	}

	const handleCardClick = e => {
		if (showCallOutPopup) {
			e.preventDefault()
			e.stopPropagation()
			setIsModalOpen(true)
		}
		// If just clickable card without callout, Link component handles navigation
	}

	const cardContent = (
		<div
			className={`${hasAnyTextContent ? "bg-white rounded-lg shadow-md" : ""} overflow-hidden h-full flex flex-col ${
				isEntireCardClickable || showCallOutPopup
					? "cursor-pointer hover:shadow-lg transition-shadow"
					: ""
			}`}
			onClick={showCallOutPopup ? handleCardClick : undefined}
		>
			{/* Image */}
			{image && image.filename && (
				<div className="w-full h-48 overflow-hidden bg-gray-200">
					<img
						src={image.filename}
						alt={image.alt || title || "Card image"}
						className="w-full h-full object-contain"
						onError={e => {
							e.target.style.display = "none"
						}}
					/>
				</div>
			)}

			{/* Grey box for consistency when no text content */}
			{!hasAnyTextContent && (
				<div className="bg-gray-200 flex-grow min-h-[120px]"></div>
			)}

			{/* Content - only show if there's text content */}
			{hasAnyTextContent && (
				<div className="p-6 flex-grow flex flex-col">
				{/* Title */}
				{title && (
					<h3 className="text-xl font-bold text-gray-900 mb-2 break-words">
						{title}
					</h3>
				)}

				{/* Subtitle */}
				{subtitle && (
					<h4 className="text-md font-semibold text-gray-700 mb-3 line-clamp-2 break-words">
						{subtitle}
					</h4>
				)}

				{/* Content */}
				{content && (
					<div className="text-gray-600 mb-4 flex-grow line-clamp-4 break-words">
						<RichText blok={blok} content={content} isRTDPage={isRTDPage} />
					</div>
				)}

				{/* Button at bottom (if button description exists and no callout) */}
				{showButtonAtBottom && (
					<div className="mt-auto pt-4">
						{isExternalUrl ? (
							<a
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-block bg-primary-500 text-white px-6 py-2 rounded transition-all duration-200 hover:bg-primary-600 hover:shadow-md"
							>
								{buttonDescription}
							</a>
						) : (
							<Link
								to={href}
								className="inline-block bg-primary-500 text-white px-6 py-2 rounded transition-all duration-200 hover:bg-primary-600 hover:shadow-md"
							>
								{buttonDescription}
							</Link>
						)}
					</div>
				)}
				</div>
			)}
		</div>
	)

	// Wrap in Link if entire card is clickable (without callout)
	if (isEntireCardClickable && !showCallOutPopup) {
		return (
			<Link to={href} className="block h-full">
				{cardContent}
			</Link>
		)
	}
	// If callout exists and has content, wrap in div with click handler
	if (hasCallOut) {
		return (
			<>
				<div className="h-full">{cardContent}</div>
				<Modal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					title={title || "Information"}
					buttons={
						<>
							{hasUrl && (
								<a
									href={href}
									target={isExternalUrl ? "_blank" : undefined}
									rel={isExternalUrl ? "noopener noreferrer" : undefined}
									className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-500 text-base font-medium text-white transition-all duration-200 hover:bg-primary-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
								>
									{buttonDescription || "Learn More"}
								</a>
							)}
							<button
								type="button"
								className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								onClick={() => setIsModalOpen(false)}
							>
								Close
							</button>
						</>
					}
				>
					<div className="max-w-2xl prose prose-lg max-w-full">
						<RichText blok={blok} content={callOut} isRTDPage={isRTDPage} />
					</div>
				</Modal>
			</>
		)
	}

	// Default: just render the card
	return cardContent
}

export default Card
